import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { formatChangeLogValue, formatYearMonth } from "../../../Utils/changeLogValueFormat";

/**
 * The months (and, for daily lines, the days) a change touches on one line item, as "from → to": before → after in
 * the line item Change log, now → after the undo in the undo dialogs.
 *
 * @param {Array|null} months - [{ yearMonth, fromValue, toValue, days: [{ day, fromValue, toValue }] }];
 *                              [] = settings only, null = not known (older log rows)
 * @param {boolean} [inverted] - light text, for a dark tooltip background
 */
const UndoChangeDetails = ({ months, inverted = false }) => {
  const mainColor = inverted ? "#FFFFFF" : "#212B36";
  const subColor = inverted ? "#DFE3E8" : "#637381";

  if (!months || months.length === 0) {
    return (
      <Typography sx={{ fontSize: "13px", lineHeight: "20px", color: subColor }}>
        {months ? "Settings only (no month value changes)" : "Details not recorded for this change"}
      </Typography>
    );
  }

  return (
    <Box>
      {months.map((month) => (
        <Box key={month.yearMonth} sx={{ marginBottom: "2px" }}>
          <Typography sx={{ fontSize: "13px", lineHeight: "20px", color: mainColor }}>
            <b>{formatYearMonth(month.yearMonth)}:</b> {formatChangeLogValue(month.fromValue)} →{" "}
            {formatChangeLogValue(month.toValue)}
          </Typography>
          {(month.days || []).map((day) => (
            <Typography
              key={day.day}
              sx={{ fontSize: "12px", lineHeight: "18px", color: subColor, paddingLeft: "16px" }}
            >
              Day {day.day}: {formatChangeLogValue(day.fromValue)} → {formatChangeLogValue(day.toValue)}
            </Typography>
          ))}
        </Box>
      ))}
    </Box>
  );
};

/** "Jan, May" for the changed months, "Settings only" when none, "-" when not known (older log rows). */
export const changedMonthsLabel = (months) => {
  if (!months) return "-";
  return months.length ? months.map((month) => formatYearMonth(month.yearMonth, false)).join(", ") : "Settings only";
};

/**
 * AG Grid cell for a "Changes" column: the short month list (cut with "…" when long); hover shows each month's
 * and day's values. Expects the row to carry {@code months}.
 */
export const ChangesCell = ({ data }) => {
  const label = changedMonthsLabel(data?.months);
  if (!data?.months) return <span>{label}</span>;
  return (
    <Tooltip title={<UndoChangeDetails months={data.months} inverted />} arrow placement="left">
      <span
        style={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: "pointer",
          textDecoration: "underline dotted",
        }}
      >
        {label}
      </span>
    </Tooltip>
  );
};

export default UndoChangeDetails;
